import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { toast } from "sonner@2.0.3";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Briefcase,
  ChevronDown,
  Layers,
  ListChecks,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import {
  ConsultantPosition,
  TaxonomySettings,
  ThematicArea,
  getAdminSettings,
  updateSetting,
  useSiteSettings,
} from "../../services/SiteSettings";

export type TaxonomySection = "thematicAreas" | "projectAssignmentTypes" | "consultantPositions";

const SECTION_INFO: Record<
  TaxonomySection,
  { title: string; item: string; plural: string; hint: string; icon: React.ComponentType<{ className?: string }> }
> = {
  thematicAreas: {
    title: "Thematic Areas",
    item: "thematic area",
    plural: "thematic areas",
    hint: "Shown in the project form and on the Expertise page.",
    icon: Layers,
  },
  projectAssignmentTypes: {
    title: "Types of Assignment",
    item: "type",
    plural: "types",
    hint: "Shown in the project form and the project filters.",
    icon: ListChecks,
  },
  consultantPositions: {
    title: "Consultant Positions",
    item: "position",
    plural: "positions",
    hint: "Choosing a position on the career form auto-fills these details.",
    icon: Briefcase,
  },
};

// Loads the latest taxonomy for admin forms and saves edits back to site settings
export function useAdminTaxonomy() {
  const { taxonomy: publicTaxonomy } = useSiteSettings();
  const [taxonomy, setTaxonomy] = useState<TaxonomySettings | null>(null);

  useEffect(() => {
    getAdminSettings()
      .then((s) => setTaxonomy(s.taxonomy))
      .catch(() => setTaxonomy(null));
  }, []);

  const saveTaxonomy = async (next: TaxonomySettings) => {
    await updateSetting("taxonomy", next);
    setTaxonomy(next);
  };

  return { taxonomy: taxonomy || publicTaxonomy, saveTaxonomy };
}

const move = <T,>(items: T[], index: number, delta: number) => {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

const cleanList = (items: string[]) => items.map((s) => s.trim()).filter(Boolean);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Stable ids keep focus and expanded state attached to the right row while reordering
let idCounter = 0;
const newId = () => `tx-${++idCounter}`;

type TypeRow = { _id: string; name: string };
type AreaRow = ThematicArea & { _id: string };
type PositionRow = ConsultantPosition & { _id: string };
type AnyRow = TypeRow | AreaRow | PositionRow;

const stripIds = (rows: AnyRow[]) => JSON.stringify(rows.map(({ _id, ...rest }) => rest));

// Editable bullet list: Enter adds the next line, Backspace on an empty line removes it
function LineListEditor({
  items,
  onChange,
  placeholder,
  addLabel,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  addLabel: string;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    if (focusIndex === null) return;
    refs.current[focusIndex]?.focus();
    setFocusIndex(null);
  }, [focusIndex, items.length]);

  const insertAfter = (i: number) => {
    const next = [...items];
    next.splice(i + 1, 0, "");
    onChange(next);
    setFocusIndex(i + 1);
  };

  const removeAt = (i: number) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="tx-lines">
      {items.map((item, i) => (
        <div key={i} className="tx-line">
          <span className="tx-line-dot" aria-hidden="true" />
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            className="tx-line-input"
            value={item}
            placeholder={placeholder}
            onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                insertAfter(i);
              } else if (e.key === "Backspace" && !item) {
                e.preventDefault();
                removeAt(i);
                setFocusIndex(Math.max(0, i - 1));
              }
            }}
          />
          <button type="button" className="tx-icon-btn tx-icon-btn-sm" onClick={() => removeAt(i)} aria-label="Remove line" title="Remove">
            <X />
          </button>
        </div>
      ))}
      <button type="button" className="tx-add-line" onClick={() => insertAfter(items.length - 1)}>
        <Plus />
        {addLabel}
      </button>
    </div>
  );
}

interface TaxonomyManagerDialogProps {
  section: TaxonomySection | null;
  taxonomy: TaxonomySettings;
  onClose: () => void;
  onSave: (next: TaxonomySettings) => Promise<void>;
}

export function TaxonomyManagerDialog({ section, taxonomy, onClose, onSave }: TaxonomyManagerDialogProps) {
  const [types, setTypes] = useState<TypeRow[]>([]);
  const [areas, setAreas] = useState<AreaRow[]>([]);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [initial, setInitial] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load drafts only when the dialog opens so background refreshes never wipe in-progress edits
  useEffect(() => {
    if (!section) return;
    const t = taxonomy.projectAssignmentTypes.map((name) => ({ _id: newId(), name }));
    const a = taxonomy.thematicAreas.map((x) => ({ ...x, subAreas: [...(x.subAreas || [])], _id: newId() }));
    const p = (taxonomy.consultantPositions || []).map((x) => ({
      ...x,
      qualifications: [...(x.qualifications || [])],
      responsibilities: [...(x.responsibilities || [])],
      _id: newId(),
    }));
    setTypes(t);
    setAreas(a);
    setPositions(p);
    setInitial(stripIds(section === "thematicAreas" ? a : section === "projectAssignmentTypes" ? t : p));
    setQuery("");
    setExpanded(null);
    setPendingDelete(null);
    setShowErrors(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`${focusId}-name`);
    if (el) {
      el.focus();
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    setFocusId(null);
  }, [focusId, expanded]);

  if (!section) return null;
  const info = SECTION_INFO[section];
  const Icon = info.icon;
  const isTypes = section === "projectAssignmentTypes";

  const rows: AnyRow[] = section === "thematicAreas" ? areas : isTypes ? types : positions;
  const setRows = (fn: (prev: any[]) => any[]) => {
    if (section === "thematicAreas") setAreas(fn);
    else if (isTypes) setTypes(fn);
    else setPositions(fn);
  };
  const dirty = stripIds(rows) !== initial;

  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  rows.forEach((r) => {
    const key = r.name.trim().toLowerCase();
    if (!key) errors[r._id] = "Name is required";
    else if (seen.has(key)) errors[r._id] = "This name is already used";
    else seen.add(key);
  });
  // Blank names only get flagged after a save attempt, so new rows don't start out red
  const errorFor = (id: string) =>
    errors[id] && (showErrors || errors[id] !== "Name is required") ? errors[id] : undefined;

  const q = query.trim().toLowerCase();
  const visible = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !q || row.name.toLowerCase().includes(q));

  const blankRow = (_id: string): AnyRow =>
    section === "thematicAreas"
      ? { _id, name: "", title: "", description: "", subAreas: [] }
      : isTypes
        ? { _id, name: "" }
        : { _id, name: "", education: "", qualifications: [], responsibilities: [], requiresThematicArea: false };

  const addRow = (afterIndex = rows.length - 1) => {
    const _id = newId();
    setRows((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, blankRow(_id));
      return next;
    });
    setQuery("");
    setExpanded(_id);
    setFocusId(_id);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r._id !== id));
    setPendingDelete(null);
  };

  const requestDelete = (row: AnyRow) => {
    // Nothing to lose on an untouched new row, so skip the confirmation
    if (stripIds([row]) === stripIds([blankRow(row._id)])) removeRow(row._id);
    else setPendingDelete(row._id);
  };

  const updateArea = (id: string, patch: Partial<AreaRow>) =>
    setAreas((prev) =>
      prev.map((a) => {
        if (a._id !== id) return a;
        const next = { ...a, ...patch };
        // Keep the public page title in step with the name unless it was customised
        if (patch.name !== undefined && (!a.title || a.title === a.name)) next.title = patch.name;
        return next;
      })
    );

  const updatePosition = (id: string, patch: Partial<PositionRow>) =>
    setPositions((prev) => prev.map((p) => (p._id === id ? { ...p, ...patch } : p)));

  const requestClose = () => {
    if (saving) return;
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    onClose();
  };

  const handleSave = async () => {
    const firstBad = rows.find((r) => errors[r._id]);
    if (firstBad) {
      setShowErrors(true);
      setQuery("");
      setExpanded(firstBad._id);
      setFocusId(firstBad._id);
      toast.error(`Please fix the highlighted ${info.plural}`);
      return;
    }

    const next: TaxonomySettings = { ...taxonomy };
    if (section === "thematicAreas") {
      next.thematicAreas = areas.map(({ _id, ...a }) => {
        const name = a.name.trim();
        return { ...a, name, title: a.title?.trim() || name, description: a.description?.trim() || "", subAreas: cleanList(a.subAreas) };
      });
    } else if (isTypes) {
      next.projectAssignmentTypes = types.map((t) => t.name.trim());
    } else {
      next.consultantPositions = positions.map(({ _id, ...p }) => ({
        ...p,
        name: p.name.trim(),
        education: p.education.trim(),
        qualifications: cleanList(p.qualifications),
        responsibilities: cleanList(p.responsibilities),
      }));
    }

    setSaving(true);
    try {
      await onSave(next);
      toast.success(`${info.title} updated`);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const renderActions = (row: AnyRow, index: number) =>
    pendingDelete === row._id ? (
      <div className="tx-confirm">
        <span>Remove?</span>
        <button type="button" className="tx-btn tx-btn-sm tx-btn-ghost" onClick={() => setPendingDelete(null)}>
          Keep
        </button>
        <button type="button" className="tx-btn tx-btn-sm tx-btn-danger" onClick={() => removeRow(row._id)}>
          Remove
        </button>
      </div>
    ) : (
      <div className="tx-actions">
        {!q && (
          <>
            <button
              type="button"
              className="tx-icon-btn"
              onClick={() => setRows((prev) => move(prev, index, -1))}
              disabled={index === 0}
              aria-label="Move up"
              title="Move up"
            >
              <ArrowUp />
            </button>
            <button
              type="button"
              className="tx-icon-btn"
              onClick={() => setRows((prev) => move(prev, index, 1))}
              disabled={index === rows.length - 1}
              aria-label="Move down"
              title="Move down"
            >
              <ArrowDown />
            </button>
          </>
        )}
        <button type="button" className="tx-icon-btn tx-icon-btn-danger" onClick={() => requestDelete(row)} aria-label="Delete" title="Delete">
          <Trash2 />
        </button>
      </div>
    );

  const renderError = (id: string) => {
    const err = errorFor(id);
    return err ? (
      <p className="tx-error">
        <AlertCircle />
        {err}
      </p>
    ) : null;
  };

  const renderTypeRow = (row: TypeRow, index: number) => (
    <li key={row._id} className={`tx-row${errorFor(row._id) ? " has-error" : ""}`}>
      <span className="tx-index">{index + 1}</span>
      <div className="tx-row-main">
        <input
          id={`${row._id}-name`}
          className="tx-input"
          value={row.name}
          placeholder="e.g. Baseline Survey"
          aria-label={`Type of assignment ${index + 1}`}
          onChange={(e) => setTypes((prev) => prev.map((t) => (t._id === row._id ? { ...t, name: e.target.value } : t)))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addRow(index);
            }
          }}
        />
        {renderError(row._id)}
      </div>
      {renderActions(row, index)}
    </li>
  );

  const renderCard = (row: AreaRow | PositionRow, index: number) => {
    const open = expanded === row._id;
    const isArea = section === "thematicAreas";
    const area = row as AreaRow;
    const position = row as PositionRow;
    const err = errorFor(row._id);

    return (
      <li key={row._id} className={`tx-card${open ? " is-open" : ""}${err ? " has-error" : ""}`}>
        <div className="tx-card-head">
          <button
            type="button"
            className="tx-card-toggle"
            onClick={() => setExpanded(open ? null : row._id)}
            aria-expanded={open}
          >
            <span className="tx-index">{index + 1}</span>
            <span className="tx-card-title">{row.name.trim() || <em>Untitled {info.item}</em>}</span>
            {isArea ? (
              <span className="tx-chip">{plural(cleanList(area.subAreas).length, "sub-area")}</span>
            ) : (
              <>
                {position.requiresThematicArea && <span className="tx-chip tx-chip-blue">Thematic area</span>}
                <span className="tx-chip">{plural(cleanList(position.qualifications).length, "qualification")}</span>
              </>
            )}
            <ChevronDown className="tx-chevron" />
          </button>
          {renderActions(row, index)}
        </div>
        {!open && err && <div className="tx-card-error">{renderError(row._id)}</div>}

        {open && (
          <div className="tx-card-body">
            <div className="tx-field">
              <label className="tx-label" htmlFor={`${row._id}-name`}>
                {isArea ? "Name" : "Position name"} <span className="tx-req">*</span>
              </label>
              <input
                id={`${row._id}-name`}
                className="tx-input"
                value={row.name}
                placeholder={isArea ? "e.g. Agriculture and Livelihood" : "e.g. Sectoral Expert"}
                onChange={(e) =>
                  isArea ? updateArea(row._id, { name: e.target.value }) : updatePosition(row._id, { name: e.target.value })
                }
              />
              {renderError(row._id)}
            </div>

            {isArea ? (
              <>
                <div className="tx-field">
                  <label className="tx-label" htmlFor={`${row._id}-desc`}>Short description</label>
                  <textarea
                    id={`${row._id}-desc`}
                    className="tx-textarea"
                    rows={2}
                    value={area.description || ""}
                    placeholder="One or two sentences shown on the Expertise page"
                    onChange={(e) => updateArea(row._id, { description: e.target.value })}
                  />
                </div>
                <div className="tx-field">
                  <span className="tx-label">Sub-thematic areas</span>
                  <LineListEditor
                    items={area.subAreas}
                    onChange={(subAreas) => updateArea(row._id, { subAreas })}
                    placeholder="Sub-thematic area"
                    addLabel="Add sub-area"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="tx-field">
                  <label className="tx-label" htmlFor={`${row._id}-edu`}>Education</label>
                  <input
                    id={`${row._id}-edu`}
                    className="tx-input"
                    value={position.education}
                    placeholder="e.g. Master's degree in a relevant field"
                    onChange={(e) => updatePosition(row._id, { education: e.target.value })}
                  />
                </div>
                <div className="tx-grid-2">
                  <div className="tx-field">
                    <span className="tx-label">Qualifications</span>
                    <LineListEditor
                      items={position.qualifications}
                      onChange={(qualifications) => updatePosition(row._id, { qualifications })}
                      placeholder="Qualification"
                      addLabel="Add qualification"
                    />
                  </div>
                  <div className="tx-field">
                    <span className="tx-label">Responsibilities</span>
                    <LineListEditor
                      items={position.responsibilities}
                      onChange={(responsibilities) => updatePosition(row._id, { responsibilities })}
                      placeholder="Responsibility"
                      addLabel="Add responsibility"
                    />
                  </div>
                </div>
                <label className="tx-toggle">
                  <input
                    type="checkbox"
                    checked={!!position.requiresThematicArea}
                    onChange={(e) => updatePosition(row._id, { requiresThematicArea: e.target.checked })}
                  />
                  <span>
                    <strong>Ask for thematic area and type of assignment</strong>
                    <span>The career form shows these extra fields when this position is selected.</span>
                  </span>
                </label>
              </>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent className="tx-dialog">
        <div className="tx-header">
          <div className="tx-header-icon">
            <Icon />
          </div>
          <div>
            <DialogTitle className="tx-title">Manage {info.title}</DialogTitle>
            <DialogDescription className="tx-subtitle">
              {info.hint} Renaming doesn't change records that already use the old name.
            </DialogDescription>
          </div>
        </div>

        <div className="tx-toolbar">
          <div className="tx-search">
            <Search />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${info.plural}...`}
              aria-label={`Search ${info.plural}`}
            />
            {query && (
              <button type="button" className="tx-icon-btn tx-icon-btn-sm" onClick={() => setQuery("")} aria-label="Clear search">
                <X />
              </button>
            )}
          </div>
          <span className="tx-count">{plural(rows.length, info.item)}</span>
          <button type="button" className="tx-btn tx-btn-primary" onClick={() => addRow()}>
            <Plus />
            Add {info.item}
          </button>
        </div>

        <div className="tx-body">
          {rows.length === 0 ? (
            <div className="tx-empty">
              <Icon />
              <p>No {info.plural} yet.</p>
              <button type="button" className="tx-btn tx-btn-outline" onClick={() => addRow()}>
                <Plus />
                Add the first {info.item}
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="tx-empty">
              <Search />
              <p>No {info.plural} match "{query}".</p>
            </div>
          ) : (
            <ol className="tx-list">
              {visible.map(({ row, index }) =>
                isTypes ? renderTypeRow(row as TypeRow, index) : renderCard(row as AreaRow | PositionRow, index)
              )}
            </ol>
          )}
          {isTypes && rows.length > 0 && <p className="tx-help tx-list-hint">Tip: press Enter in a row to add the next one.</p>}
        </div>

        <div className="tx-footer">
          <span className={`tx-status${dirty ? " is-dirty" : ""}`}>{dirty ? "Unsaved changes" : "No changes"}</span>
          <div className="tx-footer-actions">
            <button type="button" className="tx-btn tx-btn-ghost" onClick={requestClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="tx-btn tx-btn-primary" onClick={handleSave} disabled={saving || !dirty}>
              <Save />
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ManageOptionsButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="tx-manage-btn" aria-label={label} title={label}>
      <Pencil />
      Add / Edit
    </button>
  );
}
